import { useTranslation } from "react-i18next"

import type { HostedAgentEvalCaseResult, HostedAgentEvalExpectationResult } from "@/api/admin-api"

import { Badge } from "@/components/ui/badge"

import {
  HostedAgentEvalCaseDetailCodeText,
  HostedAgentEvalDetailCodeText,
  HostedAgentEvalKindLabel,
  HostedAgentEvalOutcomeBadge,
} from "./hosted-agent-eval-outcome-badge"
import {
  hostedAgentEvalOutcomeRowClass,
  isHostedAgentEvalUnmeasured,
  sortHostedAgentEvalCaseResults,
} from "../utils/hosted-agent-evals"

// Per-case and per-expectation answer sheet (#3028).
//
// Ordering is deliberate: real failures first, then everything that could not
// be measured, then the passes. An admin scanning the top of the list sees the
// two things that need a decision before the noise of what already works.

export function HostedAgentEvalCaseResultList({
  results,
}: {
  results: HostedAgentEvalCaseResult[]
}) {
  const { t } = useTranslation()

  if (results.length === 0) {
    return (
      <p className="text-muted-foreground text-xs">
        {t("adminDashboard.hostedAgents.evals.results.pending", {
          defaultValue: "No case has reported yet.",
        })}
      </p>
    )
  }

  return (
    <ul className="space-y-2">
      {sortHostedAgentEvalCaseResults(results).map((result) => (
        <li key={result.case_id}>
          <HostedAgentEvalCaseResultCard result={result} />
        </li>
      ))}
    </ul>
  )
}

/**
 * One case. The border is dashed whenever the case produced no evidence —
 * `indeterminate`, `errored` or `skipped` — so "we could not measure this"
 * reads differently from both a pass and a failure without relying on colour.
 */
export function HostedAgentEvalCaseResultCard({ result }: { result: HostedAgentEvalCaseResult }) {
  const { t, i18n } = useTranslation()
  const unmeasured = isHostedAgentEvalUnmeasured(result.status)
  const numberFormat = new Intl.NumberFormat(i18n.language)

  const meta: string[] = []
  if (result.duration_ms !== null) {
    meta.push(
      t("adminDashboard.hostedAgents.evals.results.duration", {
        defaultValue: "{{seconds}} s",
        seconds: (result.duration_ms / 1000).toFixed(1),
      })
    )
  }
  if (result.model) meta.push(result.model)
  meta.push(
    t("adminDashboard.hostedAgents.evals.results.tokens", {
      defaultValue: "{{tokens}} tokens",
      tokens: numberFormat.format(result.usage.prompt_tokens + result.usage.completion_tokens),
    })
  )
  meta.push(
    t("adminDashboard.hostedAgents.evals.results.cost", {
      defaultValue: "{{amount}} SEK",
      amount: result.usage.cost_sek.toFixed(3),
    })
  )

  return (
    <div
      className={`space-y-2 rounded-lg border p-3 ${hostedAgentEvalOutcomeRowClass(result.status)}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <HostedAgentEvalOutcomeBadge status={result.status} />
        <span className="text-sm font-medium">{result.case_name}</span>
        {result.timed_out ? (
          <Badge variant="warning">
            {t("adminDashboard.hostedAgents.evals.results.timedOut", { defaultValue: "Timed out" })}
          </Badge>
        ) : null}
        {result.tool_calls_available === false ? (
          <Badge variant="outline">
            {t("adminDashboard.hostedAgents.evals.results.noToolLedger", {
              defaultValue: "No tool ledger",
            })}
          </Badge>
        ) : null}
        {result.tool_calls_truncated ? (
          <Badge variant="outline">
            {t("adminDashboard.hostedAgents.evals.results.toolLedgerTruncated", {
              defaultValue: "Tool ledger truncated",
            })}
          </Badge>
        ) : null}
      </div>

      {/* Why the case could not be measured — from our own code map, never
          from the server's English `detail` prose. */}
      {unmeasured && result.detail_code ? (
        <p className="border-foreground bg-warning text-warning-foreground rounded-md border px-2 py-1 text-xs">
          <HostedAgentEvalCaseDetailCodeText code={result.detail_code} />
        </p>
      ) : null}

      {meta.length > 0 ? <p className="text-muted-foreground text-xs">{meta.join(" · ")}</p> : null}

      {result.expectations.length > 0 ? (
        <ul className="space-y-1.5">
          {result.expectations.map((expectation, index) => (
            <li key={`${result.case_id}-${expectation.kind}-${index}`}>
              <HostedAgentEvalExpectationRow expectation={expectation} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.hostedAgents.evals.results.noExpectations", {
            defaultValue: "This case reported no expectation results.",
          })}
        </p>
      )}
    </div>
  )
}

/**
 * One expectation. An unmeasured expectation always states WHY on its own
 * line; `expected` / `observed` are short machine-near strings from the server
 * and are shown in monospace as secondary evidence, not as the explanation.
 */
export function HostedAgentEvalExpectationRow({
  expectation,
}: {
  expectation: HostedAgentEvalExpectationResult
}) {
  const unmeasured = isHostedAgentEvalUnmeasured(expectation.outcome)
  return (
    <div
      className={`space-y-1 rounded-md border px-2 py-1.5 ${hostedAgentEvalOutcomeRowClass(
        expectation.outcome
      )}`}
    >
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <HostedAgentEvalOutcomeBadge status={expectation.outcome} />
        <span className="font-medium">
          <HostedAgentEvalKindLabel kind={expectation.kind} />
        </span>
      </div>
      {unmeasured && expectation.detail_code ? (
        <p className="border-foreground bg-warning text-warning-foreground rounded-md border px-2 py-1 text-xs">
          <HostedAgentEvalDetailCodeText code={expectation.detail_code} />
        </p>
      ) : null}
      <p className="text-muted-foreground font-mono text-[11px] break-words">
        {expectation.expected}
        {expectation.observed ? ` → ${expectation.observed}` : null}
      </p>
    </div>
  )
}
